'use client';
import { ChevronDown, ListPlus, Phone } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import AppImage from '@/components/ui/AppImage';
import Button from '@/components/ui/Button';
import { useInquiry } from '@/contexts/InquiryContext';
import { useProductContext } from '@/contexts/ProductContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { buildPublicUrl } from '@/lib/r2-urls';
import { cn } from '@/lib/utils';

const BANNER_TRANSITION_CLASS =
    'transition-[background-color,box-shadow,min-height] duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)]';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const pathname = usePathname();
    const resolvedLogoSrc = (() => {
        try {
            return (
                buildPublicUrl('/logo/trenchmate-logo-full.svg') ||
                '/logo/trenchmate-logo-full.svg'
            );
        } catch (_e) {
            return '/logo/trenchmate-logo-full.svg';
        }
    })();

    // Create state for each dropdown menu
    const [isProductsOpen, setIsProductsOpen] = useState(false);
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [isResourcesOpen, setIsResourcesOpen] = useState(false);
    // Desktop-only open state with small hide delay to avoid hover gaps
    const [isProductsDesktopOpen, setIsProductsDesktopOpen] = useState(false);
    const [isResourcesDesktopOpen, setIsResourcesDesktopOpen] = useState(false);
    const hideTimersRef = useRef({ products: null, resources: null });

    const openDesktopMenu = (menu) => {
        const t = hideTimersRef.current[menu];
        if (t) {
            clearTimeout(t);
            hideTimersRef.current[menu] = null;
        }
        if (menu === 'products') setIsProductsDesktopOpen(true);
        if (menu === 'resources') setIsResourcesDesktopOpen(true);
    };

    const closeDesktopMenu = (menu) => {
        const timer = setTimeout(() => {
            if (menu === 'products') setIsProductsDesktopOpen(false);
            if (menu === 'resources') setIsResourcesDesktopOpen(false);
            hideTimersRef.current[menu] = null;
        }, 140);
        hideTimersRef.current[menu] = timer;
    };

    useEffect(
        () => () => {
            // cleanup any pending timers on unmount
            Object.values(hideTimersRef.current).forEach(
                (t) => t && clearTimeout(t),
            );
        },
        [],
    );

    // Get products from context
    const { products } = useProductContext();

    // Get inquiry functionality
    const { getTotalItems } = useInquiry();
    const sidebar = useSidebar();

    const closeNavigationMenus = () => {
        setIsMenuOpen(false);
        setIsProductsOpen(false);
        setIsResourcesOpen(false);
    };

    const toggleMenu = () => {
        setIsMenuOpen((currentValue) => !currentValue);
        setIsProductsOpen(false);
        setIsResourcesOpen(false);
    };

    const handleHomeNavigation = (event) => {
        closeNavigationMenus();

        if (
            pathname !== '/' ||
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        event.preventDefault();
        window.scrollTo(0, 0);
    };

    // Close mobile menu on Escape or when resizing to desktop
    const menuRef = useRef(null);
    useEffect(() => {
        const handleScroll = () => setIsScrolled((window.scrollY || 0) > 20);
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        // initialize desktop state and keep in sync
        const onResize = () => {
            const desktop = window.innerWidth >= 1024;
            setIsDesktop(desktop);
            if (desktop) setIsMenuOpen(false);
        };

        const handleKey = (e) => {
            if (e.key === 'Escape') setIsMenuOpen(false);
        };

        onResize();
        window.addEventListener('resize', onResize);
        window.addEventListener('keydown', handleKey);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('keydown', handleKey);
        };
    }, []);

    // Submenu refs and measured heights for smooth animation
    const productMenuRef = useRef(null);
    const resourceMenuRef = useRef(null);
    const [productMenuMaxHeight, setProductMenuMaxHeight] = useState(0);
    const [resourceMenuMaxHeight, setResourceMenuMaxHeight] = useState(0);

    useEffect(() => {
        if (isProductsOpen) {
            const h = productMenuRef.current?.scrollHeight || 0;
            setProductMenuMaxHeight(h);
        } else {
            setProductMenuMaxHeight(0);
        }
    }, [isProductsOpen]);

    useEffect(() => {
        if (isResourcesOpen) {
            const h = resourceMenuRef.current?.scrollHeight || 0;
            setResourceMenuMaxHeight(h);
        } else {
            setResourceMenuMaxHeight(0);
        }
    }, [isResourcesOpen]);

    // Open inquiry form directly
    const handleInquiryClick = () => sidebar.open('submit');

    // Transform products from context to match the existing navbar structure
    const productItems =
        products?.map((product) => ({
            name: product.name,
            href: product.href,
            image: product.img,
        })) || [];

    const resourceItems = [
        { name: 'FAQ', href: '/resources/faq' },
        { name: 'Gallery', href: '/resources/gallery' },
        { name: 'News', href: '/news' },
        // { name: "Installation Guides", href: "/resources/guides" },
        // { name: "Safety Guidelines", href: "/resources/safety" },
        // { name: "Case Studies", href: "/resources/case-studies" },
        { name: 'Brochures', href: '/resources/brochures' },
    ];

    return (
        <nav
            id="navbar"
            className={cn(
                `sticky top-0 z-50 flex w-full items-center ${BANNER_TRANSITION_CLASS}`,
                isScrolled
                    ? 'bg-white/95 backdrop-blur-md min-h-16 shadow-md'
                    : 'bg-white min-h-20 shadow-sm',
            )}
            style={{ top: 'var(--announcement-offset, 0px)' }}
        >
            <div className="mobile-navbar relative mx-auto flex min-w-0 w-full items-center justify-between px-4 sm:px-6 lg:px-8 max-w-7xl">
                {/* Logo - Use native img tag for SVG to ensure it renders immediately
                    without waiting for client hydration. This prevents the "janky"
                    staggered load where the logo appears after other navbar content. */}
                <div className="text-white text-lg font-bold h-20 flex items-center">
                    <Link href="/" onClick={handleHomeNavigation}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={resolvedLogoSrc}
                            alt="Trenchmate"
                            width={200}
                            height={60}
                            className="h-8 sm:h-12 w-auto"
                            fetchPriority="high"
                        />
                    </Link>
                </div>

                {/* Mobile Menu & Phone button */}
                <div className="lg:hidden flex flex-row items-center h-20">
                    <Link
                        href="tel:0800385274"
                        className="flex flex-row justify-center items-center text-gray-700 hover:text-brand hover:cursor-pointer mr-2"
                    >
                        <Phone className="w-6 h-6 text-brand mr-1" />
                        <p>Call Us</p>
                    </Link>
                    <button
                        type="button"
                        onClick={toggleMenu}
                        className="lg:hidden inline-flex items-center justify-center p-2 text-gray-700 hover:text-brand hover:cursor-pointer focus:outline-none cursor-pointer transition-colors"
                        aria-label="Open/Close Navigation Menu"
                        aria-expanded={isMenuOpen}
                        aria-controls="primary-navigation"
                    >
                        <svg
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                        >
                            {isMenuOpen ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            )}
                        </svg>
                    </button>
                </div>

                {/* Desktop & Mobile Menu */}
                <div
                    id="primary-navigation"
                    ref={menuRef}
                    inert={(!isMenuOpen && !isDesktop) || undefined}
                    className={`nav-menu w-full lg:flex lg:items-center lg:w-auto mt-0 lg:static absolute left-0 top-20 z-40 lg:bg-transparent bg-white ${isMenuOpen ? 'nav-menu-open' : ''}`}
                >
                    <ul className="flex flex-col lg:flex-row lg:space-x-4 mt-2 lg:mt-0">
                        <li className="relative lg:static text-black text-md 2xl:text-lg hover:text-brand py-0 lg:py-0 lg:h-20 lg:flex lg:items-center">
                            <Link
                                href="/"
                                onClick={handleHomeNavigation}
                                className="block px-4 py-3 lg:inline-flex lg:items-center lg:h-12 lg:py-0"
                            >
                                Home
                            </Link>
                        </li>

                        {/* Products Dropdown */}
                        <li className="relative text-black text-md 2xl:text-lg hover:text-brand py-0 lg:py-0 lg:h-20 lg:flex lg:items-center">
                            {/* Desktop version - JS hover with small hide delay to avoid gaps */}
                            {/* biome-ignore lint/a11y/noStaticElementInteractions: mouse hover events for desktop menu UX, keyboard nav handled by Link children */}
                            <div
                                className="hidden lg:block"
                                onMouseEnter={() => openDesktopMenu('products')}
                                onMouseLeave={() =>
                                    closeDesktopMenu('products')
                                }
                            >
                                <Link
                                    href="/products"
                                    className={`inline-flex items-center px-4 py-2 lg:py-0 lg:items-center lg:h-12 ${isProductsDesktopOpen ? 'text-brand' : 'text-black'} hover:text-brand`}
                                >
                                    Our Products
                                    <ChevronDown
                                        className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isProductsDesktopOpen ? 'rotate-180 text-brand' : 'text-black'} hover:text-brand`}
                                    />
                                </Link>

                                {/* Desktop dropdown (state-controlled to prevent flicker) */}
                                {/* biome-ignore lint/a11y/noStaticElementInteractions: mouse hover for image preview UX, keyboard nav handled by Link children */}
                                <div
                                    className={`${isProductsDesktopOpen ? 'flex' : 'hidden'} absolute left-0 top-full min-w-93.75 bg-white shadow-lg -mt-1 z-50 transition-all duration-200`}
                                    onMouseLeave={() => setHoveredProduct(null)}
                                >
                                    {/* Product links column */}
                                    <div className="min-w-56.25 border-r-2 border-brand/10">
                                        {productItems.map((item) => (
                                            <Link
                                                key={item.href ?? item.name}
                                                href={item.href}
                                                className="block px-4 py-2 text-gray-800 hover:bg-brand/10 hover:text-brand"
                                                onMouseEnter={() =>
                                                    setHoveredProduct(item)
                                                }
                                            >
                                                {item.name}
                                            </Link>
                                        ))}
                                    </div>

                                    {/* Image preview column */}
                                    <div className="flex-1 p-2 flex items-center justify-center bg-gray-50">
                                        {hoveredProduct &&
                                        typeof hoveredProduct === 'object' ? (
                                            <div className="text-center">
                                                <AppImage
                                                    src={hoveredProduct.image}
                                                    alt={hoveredProduct.name}
                                                    width={200}
                                                    height={120}
                                                    className="w-full object-cover mb-2"
                                                />
                                                <p className="text-sm font-medium text-gray-700">
                                                    {hoveredProduct.name}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="text-center text-gray-400">
                                                <p>Hover over a product</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Mobile version - uses state */}
                            <div className="lg:hidden">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setIsProductsOpen(!isProductsOpen)
                                    }
                                    className="flex items-center justify-between w-full text-black hover:text-brand hover:cursor-pointer px-4 py-3 cursor-pointer bg-transparent border-0 transition-colors"
                                    aria-expanded={isProductsOpen}
                                >
                                    <span className="flex-1 text-left text-lg">
                                        Our Products
                                    </span>
                                    <ChevronDown
                                        className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isProductsOpen ? 'rotate-180 text-brand' : 'text-black'} hover:text-brand`}
                                        aria-hidden="true"
                                    />
                                </button>

                                <div
                                    ref={productMenuRef}
                                    className="pl-4 py-0 border-l-2 border-brand/20 overflow-hidden transition-[max-height,opacity,transform] duration-300 ease-in-out"
                                    style={{
                                        maxHeight: productMenuMaxHeight,
                                        opacity: isProductsOpen ? 1 : 0,
                                        transform: isProductsOpen
                                            ? 'translateY(0)'
                                            : 'translateY(-6px)',
                                    }}
                                >
                                    <Link
                                        href="/products"
                                        onClick={closeNavigationMenus}
                                        className="block py-1.5 text-gray-700 hover:text-brand font-medium lg:hidden"
                                    >
                                        All Products
                                    </Link>
                                    {productItems.map((item) => (
                                        <Link
                                            key={item.href ?? item.name}
                                            href={item.href}
                                            onClick={closeNavigationMenus}
                                            className="block py-1.5 text-gray-700 hover:text-brand"
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </li>

                        <li className="relative lg:static text-black text-md 2xl:text-lg hover:text-brand py-0 lg:py-0 lg:h-20 lg:flex lg:items-center">
                            <Link
                                href="/about"
                                onClick={closeNavigationMenus}
                                className="block px-4 py-3 lg:inline-flex lg:items-center lg:h-12 lg:py-0"
                            >
                                About Us
                            </Link>
                        </li>

                        {/* Resources Dropdown */}
                        <li className="relative text-black text-md 2xl:text-lg hover:text-brand py-0 lg:py-0 lg:h-20 lg:flex lg:items-center">
                            {/* Desktop version - JS hover with small hide delay to avoid gaps */}
                            {/* biome-ignore lint/a11y/noStaticElementInteractions: mouse hover events for desktop menu UX, keyboard nav handled by Link children */}
                            <div
                                className="hidden lg:block"
                                onMouseEnter={() =>
                                    openDesktopMenu('resources')
                                }
                                onMouseLeave={() =>
                                    closeDesktopMenu('resources')
                                }
                            >
                                <Link
                                    href="/resources"
                                    className={`inline-flex items-center px-4 py-2 lg:py-0 lg:items-center lg:h-12 ${isResourcesDesktopOpen ? 'text-brand' : 'text-black'} hover:text-brand`}
                                >
                                    Resources
                                    <ChevronDown
                                        className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isResourcesDesktopOpen ? 'rotate-180 text-brand' : 'text-black'} hover:text-brand`}
                                    />
                                </Link>

                                {/* Desktop dropdown (state-controlled to prevent flicker) */}
                                <div
                                    className={`${isResourcesDesktopOpen ? 'block' : 'hidden'} absolute left-0 top-full w-48 bg-white shadow-lg -mt-1 z-50 transition-all duration-200`}
                                >
                                    {resourceItems.map((item) => (
                                        <Link
                                            key={item.href ?? item.name}
                                            href={item.href}
                                            className="block px-4 py-2 text-gray-800 hover:bg-brand/10 hover:text-brand"
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Mobile version - uses state */}
                            <div className="lg:hidden">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setIsResourcesOpen(!isResourcesOpen)
                                    }
                                    className="flex items-center justify-between w-full text-black hover:text-brand hover:cursor-pointer px-4 py-3 cursor-pointer bg-transparent border-0"
                                    aria-expanded={isResourcesOpen}
                                >
                                    <span className="flex-1 text-left text-lg">
                                        Resources
                                    </span>
                                    <ChevronDown
                                        className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isResourcesOpen ? 'rotate-180 text-brand' : 'text-black'} hover:text-brand`}
                                        aria-hidden="true"
                                    />
                                </button>

                                <div
                                    ref={resourceMenuRef}
                                    className="pl-4 py-0 border-l-2 border-brand/20 overflow-hidden transition-[max-height,opacity,transform] duration-300 ease-in-out"
                                    style={{
                                        maxHeight: resourceMenuMaxHeight,
                                        opacity: isResourcesOpen ? 1 : 0,
                                        transform: isResourcesOpen
                                            ? 'translateY(0)'
                                            : 'translateY(-6px)',
                                    }}
                                >
                                    {resourceItems.map((item) => (
                                        <Link
                                            key={item.href ?? item.name}
                                            href={item.href}
                                            onClick={closeNavigationMenus}
                                            className="block py-1.5 text-gray-700 hover:text-brand"
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </li>

                        <li className="px-4 lg:px-0 lg:ml-2 py-0 lg:py-0 lg:static lg:h-20 lg:flex lg:items-center">
                            <Button
                                href="/contact"
                                variant="primary"
                                onClick={closeNavigationMenus}
                                size="sm"
                                className="w-full text-left lg:w-auto lg:text-center lg:h-12 lg:px-6 lg:text-base"
                            >
                                Contact Us
                            </Button>
                        </li>

                        <li className="px-4 lg:px-0 lg:ml-2 py-0 lg:py-0 lg:static lg:h-20 lg:flex lg:items-center mt-2 mb-3 lg:mt-0 lg:mb-0">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleInquiryClick}
                                size="sm"
                                className="relative w-full justify-between gap-3 lg:h-12 lg:w-12 lg:justify-center lg:px-0 lg:bg-brand lg:text-white lg:border-brand lg:hover:bg-white lg:hover:text-brand xl:h-12 xl:w-12"
                            >
                                <span className="inline-flex items-center gap-2">
                                    <ListPlus className="w-5 h-5" />
                                    <span className="lg:hidden">
                                        Inquiry List
                                    </span>
                                </span>
                                {getTotalItems() > 0 && (
                                    <>
                                        {/* Mobile inline badge */}
                                        <span className="lg:hidden inline-flex items-center justify-center rounded-full bg-brand text-white text-xs font-bold h-6 min-w-6 px-2">
                                            {getTotalItems()}
                                        </span>
                                        {/* Desktop absolute badge */}
                                        <span className="hidden lg:flex absolute -top-2 -right-2 bg-brand text-white text-xs font-bold rounded-full h-6 w-6 items-center justify-center">
                                            {getTotalItems()}
                                        </span>
                                    </>
                                )}
                                {/* Screen-reader live region to announce inquiry count changes */}
                                <span className="sr-only" aria-live="polite">
                                    {getTotalItems() > 0
                                        ? `${getTotalItems()} items in inquiry`
                                        : 'No items in inquiry'}
                                </span>
                            </Button>
                        </li>
                    </ul>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
